const consent = {
    id: "consent",
    type: "consent",
    required: true,
    textVersion: "1",
    items: [
        {
            key: "data_processing",
            required: true,
            text: "I agree that my answers, including health information, are shared with {{clinic.name}} and included in the message I send.",
        },
        {
            key: "marketing",
            required: false,
            text: "I would like to receive updates and offers from {{clinic.name}}.",
        },
    ],
};
/** Consent for templates outside health care (generic, salons): no mention of health information (25 October 2026). */
const generalConsent = {
    ...consent,
    items: [
        {
            key: "data_processing",
            required: true,
            text: "I agree that my answers are shared with {{clinic.name}} and included in the message I send.",
        },
        consent.items[1],
    ],
};
const yesNo = [
    { id: "yes", label: "Yes" },
    { id: "no", label: "No" },
];
/** Step 1 of every template: the essentials, as one card (14b). */
export const CONTACT_CARD_STEP = {
    id: "contact",
    title: "About you",
    presentation: "card",
    nodes: [
        {
            id: "first_name",
            type: "text",
            prompt: "First name",
            required: true,
            maps: "contact.firstName",
            message: { include: true, label: "First name" },
            maxLength: 60,
        },
        {
            id: "last_name",
            type: "text",
            prompt: "Last name",
            required: true,
            maps: "contact.lastName",
            message: { include: true, label: "Last name" },
            maxLength: 60,
        },
        {
            id: "phone",
            type: "phone",
            prompt: "WhatsApp number",
            required: true,
            maps: "contact.phone",
            message: { include: true, label: "Phone" },
            defaultCountry: "GB",
        },
        {
            id: "email",
            type: "email",
            prompt: "Email",
            required: true,
            maps: "contact.email",
            message: { include: true, label: "Email" },
        },
        {
            id: "gender",
            type: "single_choice",
            prompt: "Gender",
            required: false,
            options: [
                { id: "female", label: "Female" },
                { id: "male", label: "Male" },
                { id: "other", label: "Prefer not to say" },
            ],
            allowOther: false,
            maps: "contact.gender",
            message: { include: true, label: "Gender" },
        },
        {
            id: "age",
            type: "number",
            prompt: "Age",
            required: false,
            min: 16,
            max: 110,
            maps: "contact.age",
            message: { include: true, label: "Age" },
        },
        {
            id: "country",
            type: "text",
            prompt: "Country",
            required: false,
            maps: "contact.country",
            message: { include: true, label: "Country" },
            maxLength: 60,
        },
        {
            id: "city",
            type: "text",
            prompt: "City or region",
            required: false,
            maps: "contact.city",
            message: { include: true, label: "City" },
            maxLength: 60,
        },
    ],
};
const historyStep = (label) => ({
    id: "history",
    title: "Previous treatments",
    presentation: "chat",
    nodes: [
        {
            id: "previous",
            type: "single_choice",
            prompt: `Have you had any ${label} before?`,
            required: false,
            options: yesNo,
            allowOther: false,
            message: { include: true, label: "Previous treatments" },
        },
        {
            id: "previous_details",
            type: "long_text",
            prompt: "What was done, when, and where?",
            required: false,
            maxLength: 500,
            showIf: { op: "equals", node: "previous", value: "yes" },
            message: { include: true, label: "Previous treatment details" },
        },
        {
            id: "conditions",
            type: "long_text",
            prompt: "Any medical conditions, allergies or regular medication we should know about?",
            required: false,
            maxLength: 500,
            message: { include: true, label: "Medical notes" },
        },
    ],
});
const closingStep = {
    id: "extra",
    title: "Anything else",
    presentation: "chat",
    nodes: [
        {
            id: "photos",
            type: "file_upload",
            prompt: "You can add photos or reports if you like.",
            required: false,
            accept: ["image/*", "application/pdf"],
            maxFiles: 5,
            maxSizeMb: 15,
            message: { include: true, label: "Attachments" },
        },
        {
            id: "note",
            type: "long_text",
            prompt: "Is there anything you would like to ask or tell us before we reply?",
            required: false,
            maxLength: 800,
            message: { include: true, label: "My note" },
        },
        {
            id: "consultation_pref",
            type: "single_choice",
            prompt: "How would you prefer your consultation?",
            required: false,
            options: [
                { id: "online", label: "Online" },
                { id: "in_person", label: "In clinic" },
            ],
            allowOther: false,
            message: { include: true, label: "Preferred consultation" },
        },
        {
            id: "wrap",
            type: "message",
            text: "Thank you {{answers.first_name | firstName}}. Tap below to send everything to {{clinic.name}} on WhatsApp.",
            required: false,
        },
    ],
};
/** The generic template's closing step: the same questions in general wording. */
const generalClosingStep = {
    ...closingStep,
    nodes: closingStep.nodes.map((n) => {
        if (n.id === "photos" && n.type === "file_upload")
            return { ...n, prompt: "You can add photos or documents if you like." };
        if (n.id === "consultation_pref" && n.type === "single_choice")
            return {
                ...n,
                prompt: "How would you prefer your appointment?",
                options: [
                    { id: "online", label: "Online" },
                    { id: "in_person", label: "In person" },
                ],
                message: { include: true, label: "Preferred appointment" },
            };
        return n;
    }),
};
const choice = (id, prompt, label, options) => ({
    id,
    type: "single_choice",
    prompt,
    required: false,
    options,
    allowOther: false,
    message: { include: true, label },
});
const text = (id, prompt, label, maxLength = 300) => ({
    id,
    type: "long_text",
    prompt,
    required: false,
    maxLength,
    message: { include: true, label },
});
const picker = (prompt, label, help) => ({
    id: "procedures",
    type: "procedure_picker",
    prompt,
    required: true,
    multiple: true,
    message: { include: true, label },
    ...(help ? { help } : {}),
});
const interest = (title, nodes) => ({
    id: "interest",
    title,
    presentation: "chat",
    nodes,
});
function base(welcome, interestStep, history, general = false) {
    return {
        schemaVersion: 1,
        locale: "en-GB",
        steps: [
            {
                id: "welcome",
                title: "Welcome",
                presentation: "chat",
                nodes: [
                    { id: "hello", type: "message", text: welcome, required: false },
                    general ? generalConsent : consent,
                ],
            },
            CONTACT_CARD_STEP,
            interestStep,
            ...(history ? [history] : []),
            general ? generalClosingStep : closingStep,
        ],
        end: { actions: ["whatsapp", "booking"] },
    };
}
const TIMEFRAME = choice("timeframe", "When are you thinking of having it done?", "Timeframe", [
    { id: "asap", label: "As soon as possible" },
    { id: "3m", label: "Within 3 months" },
    { id: "later", label: "Later this year" },
    { id: "researching", label: "Just researching" },
]);
const URGENCY = choice("urgency", "How soon would you like to be seen?", "Urgency", [
    { id: "today", label: "Today or tomorrow" },
    { id: "week", label: "This week" },
    { id: "month", label: "Within a month" },
    { id: "flexible", label: "I am flexible" },
]);
const PREFERRED_TIME = text("preferred_time", "Which days and times suit you best?", "Preferred days and times", 200);
export const STARTER_FORM_KEYS = [
    "generic",
    "plastic_surgery",
    "aesthetics",
    "dermatology",
    "skin_clinic",
    "hair_transplant",
    "dental",
    "orthodontics",
    "eye_clinic",
    "bariatric",
    "fertility",
    "physiotherapy",
    "osteopathy",
    "private_gp",
    "mental_health",
    "med_spa",
    "nutrition",
    "beauty_salon",
    "hair_salon",
];
/** Picker labels for the dashboard, grouped. */
export const STARTER_TEMPLATES = {
    plastic_surgery: {
        label: "Plastic surgery",
        group: "Surgery and aesthetics",
        description: "Procedures of interest, timeframe, previous surgery, photos.",
    },
    aesthetics: {
        label: "Medical aesthetics",
        group: "Surgery and aesthetics",
        description: "Injectables and skin treatments, main concern, previous treatments.",
    },
    hair_transplant: {
        label: "Hair transplant",
        group: "Surgery and aesthetics",
        description: "Type of hair loss, previous treatments, photos of the area.",
    },
    bariatric: {
        label: "Weight loss surgery",
        group: "Surgery and aesthetics",
        description: "Procedure of interest, previous attempts, medical notes.",
    },
    dermatology: {
        label: "Dermatology",
        group: "Medical",
        description: "Skin concern, how long, seen a GP, photos.",
    },
    skin_clinic: {
        label: "Skin clinic",
        group: "Surgery and aesthetics",
        description: "Facials and skin treatments, skin type and concern.",
    },
    dental: {
        label: "Dentist",
        group: "Medical",
        description: "Treatment of interest, pain now, last check-up.",
    },
    orthodontics: {
        label: "Orthodontics",
        group: "Medical",
        description: "Braces or aligners, who the treatment is for, photos of the smile.",
    },
    eye_clinic: {
        label: "Eye clinic",
        group: "Medical",
        description: "Laser eye surgery, lenses, eye examinations.",
    },
    fertility: {
        label: "Fertility clinic",
        group: "Medical",
        description: "Treatment of interest, how long trying, previous treatment.",
    },
    physiotherapy: {
        label: "Physiotherapy",
        group: "Medical",
        description: "Where it hurts, how it started, urgency.",
    },
    osteopathy: {
        label: "Osteopathy and chiropractic",
        group: "Medical",
        description: "Complaint, how long, previous treatment.",
    },
    private_gp: {
        label: "Private GP and health clinic",
        group: "Medical",
        description: "Service needed, urgency, brief reason.",
    },
    mental_health: {
        label: "Therapy and counselling",
        group: "Medical",
        description: "What you would like help with, preferred format, safety information.",
    },
    med_spa: {
        label: "Medical spa and wellness",
        group: "Wellness and beauty",
        description: "Treatments of interest, goals, preferred times.",
    },
    nutrition: {
        label: "Nutrition and dietetics",
        group: "Wellness and beauty",
        description: "Main goal, current approach, preferred format.",
    },
    beauty_salon: {
        label: "Beauty salon",
        group: "Wellness and beauty",
        description: "Services, preferred days and times, notes.",
    },
    hair_salon: {
        label: "Hair salon",
        group: "Wellness and beauty",
        description: "Services, preferred stylist and times, photos of the look you want.",
    },
    generic: {
        label: "General (any business)",
        group: "General",
        description: "What they are interested in, a note, photos.",
    },
};
/** Services the picker starts with. Imported into the catalogue with the template; the business edits them in the builder. */
export const STARTER_PROCEDURES = {
    plastic_surgery: [
        { name: "Rhinoplasty", category: "Face" },
        { name: "Facelift", category: "Face" },
        { name: "Eyelid surgery", category: "Face" },
        { name: "Ear correction", category: "Face" },
        { name: "Breast augmentation", category: "Breast" },
        { name: "Breast reduction", category: "Breast" },
        { name: "Breast uplift", category: "Breast" },
        { name: "Liposuction", category: "Body" },
        { name: "Tummy tuck", category: "Body" },
        { name: "Arm or thigh lift", category: "Body" },
        { name: "Revision surgery", category: "Other" },
        { name: "Not sure yet", category: "Other" },
    ],
    aesthetics: [
        { name: "Anti-wrinkle injections", category: "Injectables" },
        { name: "Dermal fillers", category: "Injectables" },
        { name: "Lip enhancement", category: "Injectables" },
        { name: "Skin boosters", category: "Injectables" },
        { name: "Chemical peel", category: "Skin" },
        { name: "Microneedling", category: "Skin" },
        { name: "Laser skin treatment", category: "Skin" },
        { name: "Skin tightening", category: "Body" },
        { name: "Fat reduction", category: "Body" },
        { name: "Not sure yet", category: "Other" },
    ],
    dermatology: [
        { name: "Acne", category: "Concerns" },
        { name: "Eczema", category: "Concerns" },
        { name: "Psoriasis", category: "Concerns" },
        { name: "Rosacea", category: "Concerns" },
        { name: "Hair loss", category: "Concerns" },
        { name: "Mole check", category: "Checks" },
        { name: "Skin cancer screening", category: "Checks" },
        { name: "Mole removal", category: "Treatments" },
        { name: "Skin tag or cyst removal", category: "Treatments" },
        { name: "Laser treatment", category: "Treatments" },
    ],
    skin_clinic: [
        { name: "Deep-cleansing facial", category: "Facials" },
        { name: "Anti-ageing facial", category: "Facials" },
        { name: "Acne facial", category: "Facials" },
        { name: "Microneedling", category: "Treatments" },
        { name: "Chemical peel", category: "Treatments" },
        { name: "LED light therapy", category: "Treatments" },
        { name: "Laser hair removal", category: "Treatments" },
        { name: "Skin consultation", category: "Other" },
    ],
    hair_transplant: [
        { name: "FUE hair transplant", category: "Transplant" },
        { name: "FUT hair transplant", category: "Transplant" },
        { name: "Beard transplant", category: "Transplant" },
        { name: "Eyebrow transplant", category: "Transplant" },
        { name: "PRP for hair", category: "Non-surgical" },
        { name: "Hair loss consultation", category: "Non-surgical" },
    ],
    dental: [
        { name: "Check-up", category: "General" },
        { name: "Hygiene appointment", category: "General" },
        { name: "Filling", category: "General" },
        { name: "Root canal", category: "General" },
        { name: "Extraction", category: "General" },
        { name: "Teeth whitening", category: "Cosmetic" },
        { name: "Veneers", category: "Cosmetic" },
        { name: "Composite bonding", category: "Cosmetic" },
        { name: "Crowns", category: "Cosmetic" },
        { name: "Implants", category: "Restorative" },
        { name: "Dentures", category: "Restorative" },
        { name: "Toothache or emergency", category: "Emergency" },
    ],
    orthodontics: [
        { name: "Clear aligners", category: "Treatments" },
        { name: "Fixed braces", category: "Treatments" },
        { name: "Lingual braces", category: "Treatments" },
        { name: "Retainers", category: "Treatments" },
        { name: "Child orthodontics", category: "Treatments" },
        { name: "Consultation only", category: "Other" },
    ],
    eye_clinic: [
        { name: "Laser eye surgery", category: "Vision correction" },
        { name: "Lens replacement", category: "Vision correction" },
        { name: "Cataract surgery", category: "Surgery" },
        { name: "Eye examination", category: "Checks" },
        { name: "Glaucoma check", category: "Checks" },
        { name: "Dry eye treatment", category: "Treatments" },
    ],
    bariatric: [
        { name: "Gastric sleeve", category: "Surgery" },
        { name: "Gastric bypass", category: "Surgery" },
        { name: "Gastric band", category: "Surgery" },
        { name: "Gastric balloon", category: "Non-surgical" },
        { name: "Weight management programme", category: "Non-surgical" },
        { name: "Revision surgery", category: "Surgery" },
        { name: "Not sure yet", category: "Other" },
    ],
    fertility: [
        { name: "IVF", category: "Treatments" },
        { name: "ICSI", category: "Treatments" },
        { name: "IUI", category: "Treatments" },
        { name: "Egg freezing", category: "Preservation" },
        { name: "Sperm freezing", category: "Preservation" },
        { name: "Fertility assessment", category: "Assessment" },
        { name: "Donor treatment", category: "Treatments" },
    ],
    physiotherapy: [
        { name: "Back pain", category: "Pain" },
        { name: "Neck pain", category: "Pain" },
        { name: "Joint pain", category: "Pain" },
        { name: "Sports injury", category: "Injury" },
        { name: "Post-surgery rehabilitation", category: "Rehabilitation" },
        { name: "Pelvic health", category: "Specialist" },
        { name: "Sports massage", category: "Other" },
    ],
    osteopathy: [
        { name: "Back pain", category: "Complaints" },
        { name: "Neck pain", category: "Complaints" },
        { name: "Headaches", category: "Complaints" },
        { name: "Sciatica", category: "Complaints" },
        { name: "Posture", category: "Complaints" },
        { name: "Sports injury", category: "Complaints" },
        { name: "Pregnancy-related pain", category: "Complaints" },
    ],
    private_gp: [
        { name: "GP appointment", category: "Appointments" },
        { name: "Health check", category: "Checks" },
        { name: "Blood tests", category: "Checks" },
        { name: "Vaccinations", category: "Services" },
        { name: "Travel clinic", category: "Services" },
        { name: "Sexual health", category: "Services" },
        { name: "Referral or second opinion", category: "Services" },
        { name: "Fit note", category: "Services" },
    ],
    mental_health: [
        { name: "Anxiety", category: "Concerns" },
        { name: "Low mood", category: "Concerns" },
        { name: "Stress and burnout", category: "Concerns" },
        { name: "Relationships", category: "Concerns" },
        { name: "Trauma", category: "Concerns" },
        { name: "ADHD or autism assessment", category: "Assessments" },
        { name: "Couples therapy", category: "Formats" },
        { name: "Children and young people", category: "Formats" },
    ],
    med_spa: [
        { name: "IV drips", category: "Wellness" },
        { name: "Vitamin injections", category: "Wellness" },
        { name: "Massage", category: "Wellness" },
        { name: "Body contouring", category: "Body" },
        { name: "Laser hair removal", category: "Body" },
        { name: "Skin treatments", category: "Skin" },
        { name: "Weight management programme", category: "Programmes" },
    ],
    nutrition: [
        { name: "Weight management", category: "Goals" },
        { name: "Gut health", category: "Goals" },
        { name: "Sports nutrition", category: "Goals" },
        { name: "Pregnancy nutrition", category: "Goals" },
        { name: "Food intolerances", category: "Goals" },
        { name: "Diabetes and cholesterol", category: "Goals" },
        { name: "Eating habits", category: "Goals" },
    ],
    beauty_salon: [
        { name: "Manicure", category: "Nails" },
        { name: "Pedicure", category: "Nails" },
        { name: "Gel nails", category: "Nails" },
        { name: "Lash extensions", category: "Lashes and brows" },
        { name: "Lash lift", category: "Lashes and brows" },
        { name: "Brow lamination", category: "Lashes and brows" },
        { name: "Brow shaping", category: "Lashes and brows" },
        { name: "Facial", category: "Skin" },
        { name: "Waxing", category: "Hair removal" },
        { name: "Laser hair removal", category: "Hair removal" },
        { name: "Spray tan", category: "Other" },
    ],
    hair_salon: [
        { name: "Cut and finish", category: "Hair" },
        { name: "Colour", category: "Colour" },
        { name: "Highlights or balayage", category: "Colour" },
        { name: "Keratin or smoothing treatment", category: "Treatments" },
        { name: "Extensions", category: "Treatments" },
        { name: "Bridal or occasion hair", category: "Occasions" },
    ],
    generic: [
        { name: "General enquiry", category: "Enquiries" },
        { name: "Book an appointment", category: "Enquiries" },
        { name: "Prices", category: "Enquiries" },
        { name: "Something else", category: "Enquiries" },
    ],
};
export const STARTER_FORMS = {
    plastic_surgery: base("Hello, welcome to {{clinic.name}}. A few quick questions so our team can help you faster. It takes about two minutes.", interest("Procedures", [
        picker("Which procedures are you interested in?", "Interested in", "Not sure? Pick the closest option and tell us more at the end."),
        TIMEFRAME,
    ]), historyStep("surgery or aesthetic treatments")),
    aesthetics: base("Hello, welcome to {{clinic.name}}. Tell us a little about what you are looking for and we will reply on WhatsApp.", interest("Treatments", [
        picker("Which treatments are you interested in?", "Interested in"),
        text("concern", "What is the main concern you would like to address?", "Main concern", 500),
    ]), historyStep("aesthetic treatments")),
    dermatology: base("Hello, welcome to {{clinic.name}}. A few questions so the right specialist can see your enquiry.", interest("Your concern", [
        picker("What would you like help with?", "Concern"),
        choice("duration", "How long has this been going on?", "Duration", [
            { id: "weeks", label: "A few weeks" },
            { id: "months", label: "A few months" },
            { id: "years", label: "A year or more" },
        ]),
        choice("seen_gp", "Have you seen a GP or specialist about it?", "Seen a GP", yesNo),
    ]), historyStep("treatments for this concern")),
    skin_clinic: base("Hello, welcome to {{clinic.name}}. Tell us about your skin and what you are looking for, and we will reply on WhatsApp.", interest("Treatments", [
        picker("Which treatments are you interested in?", "Interested in"),
        choice("skin_type", "How would you describe your skin?", "Skin type", [
            { id: "dry", label: "Dry" },
            { id: "oily", label: "Oily" },
            { id: "combination", label: "Combination" },
            { id: "sensitive", label: "Sensitive" },
            { id: "unsure", label: "Not sure" },
        ]),
        text("concern", "What is your main skin concern?", "Main concern"),
    ]), historyStep("skin treatments")),
    hair_transplant: base("Hello, welcome to {{clinic.name}}. A few questions so we can give you an accurate first answer. Photos help a lot.", interest("Hair loss", [
        picker("What are you interested in?", "Interested in"),
        choice("pattern", "How would you describe your hair loss?", "Hair loss pattern", [
            { id: "hairline", label: "Receding hairline" },
            { id: "crown", label: "Thinning at the crown" },
            { id: "top", label: "Thinning on top" },
            { id: "overall", label: "Overall thinning" },
            { id: "other", label: "Something else" },
        ]),
        choice("duration", "How long have you noticed it?", "Duration", [
            { id: "under1", label: "Under a year" },
            { id: "1to5", label: "One to five years" },
            { id: "over5", label: "More than five years" },
        ]),
    ]), historyStep("hair loss treatments")),
    dental: base("Hello, welcome to {{clinic.name}}. A few quick questions and we will reply on WhatsApp with the next step.", interest("Treatment", [
        picker("What do you need help with?", "Treatment"),
        choice("pain", "Are you in pain right now?", "In pain", yesNo),
        choice("last_checkup", "When was your last check-up?", "Last check-up", [
            { id: "6m", label: "In the last six months" },
            { id: "1y", label: "About a year ago" },
            { id: "longer", label: "Longer ago" },
            { id: "never", label: "Never or not sure" },
        ]),
    ]), historyStep("dental treatments")),
    orthodontics: base("Hello, welcome to {{clinic.name}}. A few questions so we can tell you which options may suit you.", interest("Treatment", [
        picker("Which option are you interested in?", "Interested in"),
        choice("for_whom", "Who is the treatment for?", "Treatment for", [
            { id: "me", label: "Myself" },
            { id: "child", label: "My child" },
        ]),
        choice("previous_ortho", "Have you had braces or aligners before?", "Previous orthodontics", yesNo),
    ])),
    eye_clinic: base("Hello, welcome to {{clinic.name}}. A few questions so the right specialist can look at your enquiry.", interest("Your eyes", [
        picker("What are you interested in?", "Interested in"),
        choice("correction", "Do you currently wear glasses or contact lenses?", "Current correction", [
            { id: "glasses", label: "Glasses" },
            { id: "lenses", label: "Contact lenses" },
            { id: "both", label: "Both" },
            { id: "none", label: "Neither" },
        ]),
    ]), historyStep("eye treatments or surgery")),
    bariatric: base("Hello, welcome to {{clinic.name}}. A few questions so our team can prepare a proper first answer for you.", interest("Procedure", [
        picker("Which option are you interested in?", "Interested in"),
        text("tried", "What have you tried so far?", "Tried so far"),
        TIMEFRAME,
    ]), historyStep("weight loss treatments or surgery")),
    fertility: base("Hello, welcome to {{clinic.name}}. We know this is personal. A few questions help the right person reply to you.", interest("Treatment", [
        picker("What would you like to talk to us about?", "Interested in"),
        choice("trying", "How long have you been trying?", "Trying for", [
            { id: "under1", label: "Under a year" },
            { id: "1to2", label: "One to two years" },
            { id: "over2", label: "More than two years" },
            { id: "na", label: "Not applicable" },
        ]),
    ]), historyStep("fertility treatments")),
    physiotherapy: base("Hello, welcome to {{clinic.name}}. A few questions so we can match you with the right physiotherapist.", interest("Your problem", [
        picker("What do you need help with?", "Problem"),
        choice("onset", "How did it start?", "Onset", [
            { id: "injury", label: "An injury" },
            { id: "gradual", label: "Gradually" },
            { id: "surgery", label: "After surgery" },
            { id: "unsure", label: "Not sure" },
        ]),
        URGENCY,
    ]), historyStep("treatment for this problem")),
    osteopathy: base("Hello, welcome to {{clinic.name}}. A few questions and we will reply on WhatsApp with the next available times.", interest("Your complaint", [
        picker("What is troubling you?", "Complaint"),
        choice("duration", "How long has it been going on?", "Duration", [
            { id: "days", label: "A few days" },
            { id: "weeks", label: "A few weeks" },
            { id: "months", label: "Months or longer" },
        ]),
        URGENCY,
    ]), historyStep("treatment for this complaint")),
    private_gp: base("Hello, welcome to {{clinic.name}}. A few questions so we can book the right appointment for you.", interest("Appointment", [
        picker("What do you need?", "Service"),
        URGENCY,
        text("reason", "Briefly, what is the appointment about?", "Reason"),
    ])),
    mental_health: base("Hello, welcome to {{clinic.name}}. Thank you for reaching out. A few questions help us match you with the right therapist.", interest("How we can help", [
        {
            id: "safety",
            type: "message",
            text: "If you are in immediate danger or feel you may harm yourself, please call 999 or go to A&E. For urgent support, call NHS 111 or the Samaritans on 116 123.",
            required: false,
        },
        picker("What would you like help with?", "Help with"),
        choice("format", "How would you prefer to work together?", "Preferred format", [
            { id: "online", label: "Online" },
            { id: "in_person", label: "In person" },
            { id: "either", label: "Either" },
        ]),
        choice("previous_therapy", "Have you had therapy before?", "Previous therapy", yesNo),
    ])),
    med_spa: base("Hello, welcome to {{clinic.name}}. Tell us what you are looking for and we will reply on WhatsApp.", interest("Treatments", [
        picker("Which treatments are you interested in?", "Interested in"),
        text("goal", "What would you like to achieve?", "Goal"),
        PREFERRED_TIME,
    ]), historyStep("similar treatments")),
    nutrition: base("Hello, welcome to {{clinic.name}}. A few questions so we can suggest the right plan for you.", interest("Your goal", [
        picker("What would you like help with?", "Help with"),
        text("goal", "What is your main goal, in your own words?", "Main goal", 500),
        choice("format", "How would you prefer to work together?", "Preferred format", [
            { id: "online", label: "Online" },
            { id: "in_person", label: "In person" },
            { id: "either", label: "Either" },
        ]),
    ])),
    beauty_salon: base("Hello, welcome to {{clinic.name}}. Tell us what you would like and when, and we will confirm on WhatsApp.", interest("Services", [picker("Which services would you like?", "Services"), PREFERRED_TIME]), undefined, true),
    hair_salon: base("Hello, welcome to {{clinic.name}}. Tell us what you would like and when, and we will confirm on WhatsApp. A photo of the look you want helps.", interest("Services", [
        picker("Which services would you like?", "Services"),
        text("stylist", "Do you have a preferred stylist?", "Preferred stylist", 100),
        PREFERRED_TIME,
    ]), undefined, true),
    generic: base("Hello, welcome to {{clinic.name}}. A few quick questions and we will get straight back to you.", interest("Your enquiry", [picker("What are you interested in?", "Interested in")]), undefined, true),
};
/**
 * A ready contact form for a page (owner decision, 16 October 2026): the usual contact fields,
 * shown as a classic form. Consent comes first in the schema (publishing requires it before any
 * question); the page form shows it just above the send button.
 */
export const CONTACT_PAGE_FORM = {
    schemaVersion: 1,
    locale: "en-GB",
    steps: [
        {
            id: "contact",
            title: "Contact",
            presentation: "chat",
            nodes: [
                {
                    id: "consent",
                    type: "consent",
                    required: true,
                    textVersion: "1",
                    items: [
                        {
                            key: "data_processing",
                            required: true,
                            text: "I agree that my details and message are shared with {{clinic.name}}, and included in a WhatsApp message if I choose to continue there.",
                        },
                        {
                            key: "marketing",
                            required: false,
                            text: "I would like to receive updates and offers from {{clinic.name}}.",
                        },
                    ],
                },
                {
                    id: "first_name",
                    type: "text",
                    prompt: "First name",
                    required: true,
                    maps: "contact.firstName",
                    message: { include: true, label: "First name" },
                    maxLength: 60,
                },
                {
                    id: "last_name",
                    type: "text",
                    prompt: "Last name",
                    required: true,
                    maps: "contact.lastName",
                    message: { include: true, label: "Last name" },
                    maxLength: 60,
                },
                {
                    id: "email",
                    type: "email",
                    prompt: "Email",
                    required: true,
                    maps: "contact.email",
                    message: { include: true, label: "Email" },
                },
                {
                    id: "phone",
                    type: "phone",
                    prompt: "Mobile number",
                    required: true,
                    maps: "contact.phone",
                    defaultCountry: "GB",
                    message: { include: true, label: "Phone" },
                },
                {
                    id: "procedures",
                    type: "procedure_picker",
                    prompt: "What would you like to ask about?",
                    required: false,
                    multiple: true,
                    message: { include: true, label: "Interested in" },
                },
                {
                    id: "note",
                    type: "long_text",
                    prompt: "Your message",
                    required: false,
                    message: { include: true, label: "My note" },
                },
            ],
        },
    ],
    end: { actions: ["whatsapp"] },
};
//# sourceMappingURL=starter-forms.js.map